import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { RELEASES, BOOKS, formatReleaseDate } from "@/lib/content";
import { absoluteUrl, SITE_URL } from "@/lib/site";
import {
  TIMELINE_ID,
  breadcrumbs,
  ldScript,
  orgRef,
  pageNode,
  personRef,
  releaseId,
  seoMeta,
} from "@/lib/schema";

const TITLE = "Buddha KA Release Timeline | Discography & Publications by Year";
const DESCRIPTION =
  "The dated release timeline of Buddha KA — every KAfrequency album, EP, single, and book in the order it entered the world.";

export const Route = createFileRoute("/timeline")({
  head: () => {
    // Chronological entries built from the same first-party catalog the page
    // renders, so the structured data can never drift from the visible list.
    const dated = [
      ...RELEASES.filter((r) => r.year || r.releaseDate).map((r) => ({
        year: r.year ?? new Date(r.releaseDate!).getFullYear(),
        date: r.releaseDate,
        name: r.title,
        path: `/music/${r.slug}`,
        id: releaseId(r.slug),
      })),
      ...BOOKS.filter((b) => (b as { year?: number }).year).map((b) => ({
        year: (b as { year?: number }).year!,
        date: undefined as string | undefined,
        name: b.title,
        path: `/books/${b.slug}`,
        id: undefined as string | undefined,
      })),
    ].sort((a, b) => a.year - b.year);

    return {
      ...seoMeta({ title: TITLE, description: DESCRIPTION, path: "/timeline" }),
      scripts: [
        ldScript(
          pageNode({
            type: "CollectionPage",
            id: TIMELINE_ID,
            url: `${SITE_URL}/timeline`,
            name: "Buddha KA Release Timeline",
            description: DESCRIPTION,
            about: [personRef(), orgRef()],
            extra: {
              creator: personRef(),
              publisher: orgRef(),
              mainEntity: {
                "@type": "ItemList",
                itemListOrder: "https://schema.org/ItemListOrderAscending",
                numberOfItems: dated.length,
                itemListElement: dated.map((m, i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  name: m.name,
                  url: absoluteUrl(m.path),
                  ...(m.id ? { item: { "@id": m.id } } : {}),
                })),
              },
            },
          }),
        ),
        ldScript(breadcrumbs([["Release timeline", "/timeline"]])),
      ],
    };
  },
  component: TimelinePage,
});


interface Milestone {
  key: string;
  year: number;
  date: string;
  kind: "music" | "book" | "chapter";
  title: string;
  subtitle: string;
  href?: string;
  hrefRoute?: "/music/$slug" | "/books/$slug";
  slug?: string;
}

const CHAPTERS: Milestone[] = [
  { key: "chapter-genesis", year: 2019, date: "The Beginning", kind: "chapter", title: "Genesis of KAfrequency", subtitle: "A body of work forms in silence." },
  { key: "chapter-temple", year: 2024, date: "Present", kind: "chapter", title: "The Digital Temple opens", subtitle: "Music, activations, teachings, artifacts — one field." },
];

function TimelinePage() {
  const milestones = useMemo<Milestone[]>(() => {
    const items: Milestone[] = [];
    for (const r of RELEASES) {
      if (!r.year && !r.releaseDate) continue;
      items.push({
        key: `music:${r.slug}`,
        year: r.year ?? new Date(r.releaseDate!).getFullYear(),
        date: formatReleaseDate(r) ?? String(r.year ?? ""),
        kind: "music",
        title: r.title,
        subtitle: `${r.type} · ${r.frequency}`,
        hrefRoute: "/music/$slug",
        slug: r.slug,
      });
    }
    for (const b of BOOKS) {
      const year = (b as { year?: number }).year;
      if (!year) continue;
      items.push({
        key: `book:${b.slug}`,
        year,
        date: String(year),
        kind: "book",
        title: b.title,
        subtitle: b.subtitle ?? "Book",
        hrefRoute: "/books/$slug",
        slug: b.slug,
      });
    }
    return [...items, ...CHAPTERS].sort((a, b) => a.year - b.year);
  }, []);

  return (
    <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-20">
      <header className="text-center" data-reveal="up">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">The Path</p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl">Sacred Timeline</h1>
        <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
          Every transmission in order — how the field has unfolded.
        </p>
      </header>

      <ol className="relative mt-14 border-l hairline pl-6 sm:pl-10">
        <span className="pointer-events-none absolute left-0 top-0 h-full w-px bg-gradient-to-b from-[color:var(--gold-luminous)] via-[color:var(--gold)] to-transparent opacity-60" aria-hidden />
        {milestones.map((m) => (
          <li key={m.key} className="relative mb-12 last:mb-0" data-reveal="up">
            <span
              className={`absolute -left-[7px] top-2 h-3 w-3 rounded-full border ${
                m.kind === "chapter" ? "bg-gold border-gold" : "bg-charcoal border-gold"
              }`}
              aria-hidden
            />
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{m.date}</p>
            {m.hrefRoute && m.slug ? (
              <Link
                to={m.hrefRoute}
                params={{ slug: m.slug }}
                className="mt-2 block hover-glow"
              >
                <h2 className="font-serif text-2xl text-ivory hover:text-gold-luminous sm:text-3xl">{m.title}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{m.subtitle}</p>
              </Link>
            ) : (
              <div className="mt-2">
                <h2 className="font-serif text-2xl text-gold-luminous sm:text-3xl">{m.title}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{m.subtitle}</p>
              </div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
